import React, { useEffect, useState } from "react";
import { Container, Input, Button, Badge, Table, Row, Col } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import LoadingSpinner from "../../components/LoadingSpinner";
import { GetPayments } from "../../requests";
import { toast } from "react-toastify";

const STATUS_COLORS = {
  PAID: "success",
  PENDING: "warning",
  NOT_COLLECTED: "secondary",
  REFUNDED: "info",
  FAILED: "danger",
};

const REFUND_COLORS = {
  NONE: "secondary",
  INITIATED: "warning",
  COMPLETED: "success",
  FAILED: "danger",
};

const Payments = () => {
  document.title = "Payments | Susipizza";

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const resp = await GetPayments(token);
      setPayments(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const formatTime = (ts) => {
    if (!ts) return "—";
    const date = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return date.toLocaleString();
  };

  const filtered = payments.filter((p) => {
    if (statusFilter && p.status !== statusFilter) return false;
    if (methodFilter && p.method !== methodFilter) return false;
    if (search) {
      const query = search.toLowerCase();
      const haystack = `${p.orderCode || ""} ${p.customerName || ""} ${p.gatewayPaymentId || ""}`.toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Administration" breadcrumbItem="Payments" />

          <Row className="mb-3 align-items-center">
            <Col md={4}>
              <Input
                placeholder="Search by order code, customer, payment ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
              />
            </Col>
            <Col md={3}>
              <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="PAID">Paid</option>
                <option value="PENDING">Pending</option>
                <option value="NOT_COLLECTED">Not Collected</option>
                <option value="REFUNDED">Refunded</option>
                <option value="FAILED">Failed</option>
              </Input>
            </Col>
            <Col md={3}>
              <Input type="select" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
                <option value="">All methods</option>
                <option value="ONLINE">Online (Razorpay)</option>
                <option value="COD">Cash on Delivery</option>
              </Input>
            </Col>
            <Col md={2} className="text-md-end mt-2 mt-md-0">
              <Button color="light" size="sm" onClick={load}>
                <i className="mdi mdi-refresh me-1" /> Refresh
              </Button>
            </Col>
          </Row>

          <div className="text-muted small mb-2">
            Showing {filtered.length} of {payments.length} payment{payments.length === 1 ? "" : "s"}
          </div>

          {loading ? (
            <LoadingSpinner loading={true} />
          ) : filtered.length === 0 ? (
            <p className="text-muted text-center py-5">No payments match the current filters.</p>
          ) : (
            <div style={{ overflowX: "auto", background: "white", borderRadius: 12 }}>
              <Table className="table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Store</th>
                    <th>Method</th>
                    <th>Gateway Payment ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Refund</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id}>
                      <td>{formatTime(p.createdAt)}</td>
                      <td>{p.orderCode || p.orderId}</td>
                      <td>{p.customerName || "—"}</td>
                      <td>{p.storeName || "—"}</td>
                      <td>{p.method === "ONLINE" ? "Online" : "COD"}</td>
                      <td><code>{p.gatewayPaymentId || "—"}</code></td>
                      <td>₹{(p.amount || 0).toFixed(2)}</td>
                      <td>
                        <Badge color={STATUS_COLORS[p.status] || "secondary"} pill>
                          {p.status}
                        </Badge>
                      </td>
                      <td>
                        {p.refund && p.refund.status !== "NONE" ? (
                          <Badge color={REFUND_COLORS[p.refund.status] || "secondary"} pill>
                            {p.refund.status} {p.refund.amount ? `(₹${p.refund.amount})` : ""}
                          </Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Payments;
